export interface PreparationTimers {
  /** The default preparation timer in seconds. */
  default: number;
  /** Round-specific overrides (round number → timer in seconds). */
  [round: number]: number;
}

export const GameConfig = {
  startingLife: 5,
  startingGold: 3,
  startingHandSize: 3,
  maxHandSize: 8,
  /**
   * Maximum number of minions a player may have on the battlefield at once.
   * This is THE setting for board capacity — the server (PlayerState,
   * GameSessionService), the bots (BotService) and the UI (BattlefieldUI)
   * all read it from here.
   */
  maxBoardSize: 7,
  playerCount: 4,
  upgradeCost: 3,
  maxUpgrades: 5,
  /** Base gold cost of buying a new (random) card during preparation. */
  cardPurchaseCost: 5,
  /**
   * Floor for the card purchase cost: Discount minions reduce the cost, but
   * it can never drop below this value.
   */
  cardPurchaseMinCost: 2,
  /**
   * BASE per-game cap on how many runes a player may purchase. The rune
   * shop lists the WHOLE catalogue, but once a player owns this many runes
   * the server rejects further purchases and the UI locks the remaining
   * rows. The Buy Rune button shows the progress as "(x/N)". This is the
   * BASE value — abilities may raise it later (it is read from this config
   * everywhere, so a future ability only has to override it).
   */
  maxRunesPurchasable: 3,
  combatTimeout: 120, // in seconds
  preparationTimers: {
    default: 30,
    // Round-specific overrides can be added here
  } as PreparationTimers,
  /**
   * Dead time (in seconds) AFTER the preparation countdown reaches 0.
   * Preparation actions stay available for the ENTIRE countdown (while
   * remaining > 0); when the countdown hits 0 the actions lock and combat
   * waits this many extra seconds before it starts. (Historically this was
   * a "lock N seconds before combat" threshold — the lock now happens at 0
   * and this value is the post-zero dead time.)
   */
  preparationLockThreshold: 2,
  /**
   * Sudden-death fail-safe threshold: after this many minion attacks have
   * been made in a single combat, sudden-death mode activates. From the
   * NEXT attack on, every living minion (both sides, including the
   * attacker) takes (attackNumber - suddenDeathAttackThreshold) damage in
   * addition to normal combat damage — attack 33 takes 1, attack 34 takes
   * 2, and so on. This guarantees a combat can never run forever
   * (e.g., two 0-attack boards that can never deal damage to each other).
   */
  suddenDeathAttackThreshold: 32,
};

export type GameConfigType = typeof GameConfig;

export const getGameConfig = (overrides?: Partial<GameConfigType>): GameConfigType => {
  return { ...GameConfig, ...overrides };
};

/**
 * Calculates the sudden-death damage for a given (1-indexed) attack number
 * in a combat.
 *
 * Returns 0 while the combat is still below the threshold (normal combat),
 * and `attackNumber - threshold` once past it: with the default threshold of
 * 32, attack #33 deals 1 sudden-death damage to ALL living minions, attack
 * #34 deals 2, and so on. The damage is applied IN ADDITION to the normal
 * simultaneous attack damage, and the attacking minion is hit as well.
 *
 * @param attackNumber The 1-indexed attack number within the combat.
 * @param config The game configuration (defaults to GameConfig).
 * @returns The sudden-death damage to deal to every living minion (0 = mode inactive).
 */
export function getSuddenDeathDamage(
  attackNumber: number,
  config: GameConfigType = GameConfig
): number {
  if (!Number.isFinite(attackNumber) || attackNumber <= 0) {
    return 0;
  }
  return Math.max(0, attackNumber - config.suddenDeathAttackThreshold);
}

/**
 * Looks up the preparation timer duration for a given round.
 *
 * Timer tiers (checked from highest to lowest):
 * - Round >= 8: 60 seconds
 * - Round >= 4: 45 seconds
 * - Round 1-3: 30 seconds (default)
 *
 * Round-specific overrides in the config take priority over tier defaults.
 *
 * @param round The round number (1-indexed).
 * @param config The game configuration (defaults to GameConfig).
 * @returns The preparation timer duration in seconds.
 */
export function getPreparationTimer(
  round: number,
  config: GameConfigType = GameConfig
): number {
  const timers = config.preparationTimers;

  // Round-specific overrides take highest priority
  if (timers[round] !== undefined) {
    return timers[round];
  }

  // Tier-based defaults (checked from highest to lowest)
  if (round >= 8) return 60;
  if (round >= 4) return 45;
  return timers.default; // 30 seconds for rounds 1-3
}
